import { Avatar, Colors, Text } from '@amelio/ui-library';

import type { AvatarSize, TextProps } from '@amelio/ui-library';

import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/** The subset of an employee this component needs; matches `ThematicMeetingEmployee`,
 * `OverdueEmployee` and the plan header's employee alike. */
export interface IdentifiedEmployee {
    firstName: string;
    lastName: string;
    profilePictureUrl?: string | null;
    jobTitleId?: string | null;
}

/**
 * Space between the avatar and the name, as complete class strings so the Tailwind scanner sees them.
 * `roomy` is the meeting card's, at the `gap-4.5` step (18px); `default` keeps `gap-3` (12px).
 */
const AVATAR_SPACING_CLASSES = {
    default: 'gap-3',
    roomy: 'gap-4.5',
} as const;

/**
 * Name sizes named here rather than left to the library's own text defaults, so the name keeps the
 * same size as the body text around it: the scale's 12px and 14px, and no line height of their own.
 */
const NAME_SIZE_CLASSES = {
    small: 'text-[12px]',
    default: 'text-[14px]',
} as const;

interface EmployeeIdentityProps {
    employee: IdentifiedEmployee | null | undefined;
    size?: AvatarSize;
    nameSize?: keyof typeof NAME_SIZE_CLASSES;
    nameWeight?: TextProps['weight'];
    /** Shows the employee's job title under the name, when the company knows it. */
    showJobTitle?: boolean;
    /** Lets a long name wrap onto a second line instead of ending in an ellipsis. */
    wrapName?: boolean;
    /** De-emphasises the row (dimmed avatar, lighter name colour) — used for a completed meeting. */
    muted?: boolean;
    avatarSpacing?: keyof typeof AVATAR_SPACING_CLASSES;
    dataTestId?: string;
}

/**
 * Avatar + name + optional subtitle line, shared by every place in the feature that shows an
 * employee (meeting cards, recent activity, overdue dialog, plan header) so the fallback name,
 * the initials and the avatar colour stay identical across them.
 */
export const EmployeeIdentity = ({
    employee,
    size = 'sm',
    nameSize = 'small',
    nameWeight = 'medium',
    showJobTitle = false,
    wrapName = false,
    muted = false,
    avatarSpacing = 'default',
    dataTestId,
}: EmployeeIdentityProps) => {
    const { getJobTitle } = useCompanyInformation();
    const employeeName = getFullName(employee ?? undefined);
    // The API sends the job title as an id; its label lives in the company information.
    const jobTitle = showJobTitle ? getJobTitle(employee?.jobTitleId ?? '') : '';

    return (
        <div
            className={`flex min-w-0 items-center ${AVATAR_SPACING_CLASSES[avatarSpacing]}`}
            data-testid={dataTestId}
        >
            <Avatar
                src={getProfilePictureUrl(employee?.profilePictureUrl) || undefined}
                alt={employeeName}
                initials={employee ? getInitials(employee) : ''}
                bgColor={'purple'}
                size={size}
                className={muted ? 'opacity-80' : undefined}
            />

            <div className={'flex min-w-0 flex-col gap-0.5'}>
                {/* `truncate` only clips visually; the full name stays in the accessible name
                    computation via the text content, so `title` is here purely for sighted
                    mouse users who can't see the clipped part otherwise. */}
                <div title={employeeName}>
                    <Text
                        weight={nameWeight}
                        color={muted ? Colors.BASE_500 : Colors.BASE_600}
                        className={NAME_SIZE_CLASSES[nameSize]}
                        truncate={!wrapName}
                    >
                        {employeeName}
                    </Text>
                </div>

                {jobTitle && (
                    <div title={jobTitle}>
                        <Text
                            color={Colors.BASE_500}
                            className={'text-[12px]'}
                            truncate
                        >
                            {jobTitle}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};
